import React, {ChangeEvent, FormEvent, useCallback, useState} from 'react';
import {createMerchant} from "../../api/MerchantsApi";
import styles from "./AddMerchantAccountComponent.module.css";

type FormData = {
    merchantId: string;
    apiKey: string;
    reportApiKey: string;
    clientKey: string;
    usAcquired: boolean;
};

const INITIAL_FORM_DATA: FormData = {
    merchantId: '',
    apiKey: '',
    reportApiKey: '',
    clientKey: '',
    usAcquired: false
};

type TextFieldProps = {
    label: string;
    type: string;
    name: keyof FormData;
    value: string;
    onChange: (e: ChangeEvent<HTMLInputElement>) => void;
    placeholder: string;
};

type SwitchProps = {
    label: string;
    name: keyof FormData;
    value: boolean;
    onChange: (e: ChangeEvent<HTMLInputElement>) => void;
}

const TextField: React.FC<TextFieldProps> = ({label, type, name, value, onChange, placeholder}) => (
    <div className="form-field">
        <label>{label}</label>
        <input type={type} name={name} value={value} onChange={onChange} placeholder={placeholder}/>
    </div>
);

const SwitchField: React.FC<SwitchProps> = ({label, name, value, onChange}) => (
    <div className="form-field">
        <label>{label}</label>

        <label className={styles.switchRow}>
            <input
                type="checkbox"
                name={name}
                checked={value}
                onChange={onChange}
                className={styles.srOnly}
            />
            <span className={styles.track} aria-hidden="true">
        <span className={styles.knob}/>
      </span>
            <span className={styles.switchText}>{value ? "Yes" : "No"}</span>
        </label>
    </div>
);

type AddMerchantAccountProps = {
    showAddPopup: boolean;
    handleCloseAdd: () => void;
    fetchMerchants: () => void;
};

const AddMerchantAccountComponent: React.FC<AddMerchantAccountProps> = ({
                                                                            showAddPopup,
                                                                            handleCloseAdd,
                                                                            fetchMerchants
                                                                        }) => {
    const [formData, setFormData] = useState<FormData>(INITIAL_FORM_DATA);

    const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        const {name, value} = e.target;
        setFormData((prevData) => ({...prevData, [name]: value}));
    }, []);

    const handleConfirmAdd = useCallback(async (event: FormEvent) => {
        event.preventDefault();
        if (!formData.merchantId.trim() || !formData.apiKey.trim()) {
            alert('Fields merchantId and apiKey are required');
            return;
        }
        try {
            await createMerchant(formData);
            fetchMerchants();
            setFormData(INITIAL_FORM_DATA);
            handleCloseAdd();
        } catch (error: Error | any) {
            alert(error.message ?? 'An error occurred while adding the merchant account.');
        }
    }, [formData, fetchMerchants, handleCloseAdd]);

    const handleClose = () => {
        setFormData(INITIAL_FORM_DATA);
        handleCloseAdd();
    };

    if (!showAddPopup) return <></>;

    return (
        <div id="add-popup" className="popup">
            <div className="popup-content">
                <h2>Add Merchant Account</h2>
                <button className="close-btn" onClick={handleClose}>X</button>
                <p>Make sure that you’re logged in to the Customer Area because you need to copy several settings from
                    there to fill in the following information.</p>
                <form onSubmit={handleConfirmAdd}>
                    <TextField label="Merchant Account (required)" type="text" name="merchantId"
                               value={formData.merchantId}
                               onChange={handleChange} placeholder="Merchant Account"/>
                    <TextField label="Merchant API Key (required)" type="text" name="apiKey" value={formData.apiKey}
                               onChange={handleChange} placeholder="Merchant API Key"/>
                    <TextField label="Client Key" type="text" name="clientKey" value={formData.clientKey}
                               onChange={handleChange} placeholder="Client Key"/>
                    <TextField label="Report API Key" type="text" name="reportApiKey" value={formData.reportApiKey}
                               onChange={handleChange} placeholder="Report API Key"/>
                    <SwitchField label="US based merchant" name="usAcquired" value={formData.usAcquired} onChange={(e) =>
                        setFormData((prev) => ({...prev, usAcquired: e.target.checked}))
                    }/>
                    <div className="form-actions">
                        <button type="submit" className="btn btn-primary">Add Merchant Account</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddMerchantAccountComponent;